/**
 * Previous Fundings note — per-payapp table state and the auto-comment built from it.
 *
 * Each payapp has its own previous fundings table. A single-payapp report keeps the original wording;
 * with several payapps the comment names the applications that differ and lists one labelled
 * recommendation/received block per application.
 *
 * Kept free of component imports so it can be unit-tested directly.
 */
import { formatCurrency } from '../../../../common/utils/common.util';
import { ProjectPreviousFundingDTO } from '../../../project/project.type';

export const AUTO_COMMENT =
  'We have confirmed in our G702 Review Form that the amount shown received by the Contractor for the previous Application for Payment is the same amount we recommended funding in our previous report.';

export const AUTO_COMMENT_MULTI =
  'We have confirmed in our G702 Review Form that the amounts shown received by the Contractors for the previous Applications for Payment are the same amount we recommended funding in our previous report.';

/** Every "no difference" comment — any of them counts as an unedited auto-comment. */
export const MATCH_AUTO_COMMENTS = [AUTO_COMMENT, AUTO_COMMENT_MULTI];

export type DiscrepancyCommentVariant = 'increase' | 'reduction' | 'borrowerHolding' | 'borrowerUsedOutsideFunds' | 'neutral';

/** single: one-payapp report · oneOfMany: one of several payapps differs · many: several differ. */
export type DiscrepancyCommentScope = 'single' | 'oneOfMany' | 'many';

export const ALL_DISCREPANCY_COMMENT_VARIANTS: DiscrepancyCommentVariant[] = [
  'increase',
  'reduction',
  'borrowerHolding',
  'borrowerUsedOutsideFunds',
  'neutral',
];

const SCOPE_APPLICATIONS_PHRASE: Record<DiscrepancyCommentScope, string> = {
  single: '',
  oneOfMany: ' for the following application',
  many: ' for the following applications',
};

const discrepancyOpening = (scope: DiscrepancyCommentScope) =>
  `Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report${SCOPE_APPLICATIONS_PHRASE[scope]}.`;

const DISCREPANCY_COMMENT_PREFIX_BODIES: Record<DiscrepancyCommentVariant, string> = {
  increase:
    'Upon noticing the discrepancy, we contacted the lender to confirm whether an increase was made without our knowledge and find that the Lender did increase the funding amount as follows:',
  reduction:
    'Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender did reduce the funding amount as follows:',
  borrowerHolding:
    'Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender funding amount and our recommendation were the same.  We understand the Borrower is still holding funds from the last lender draw which were not passed along to the Contractor.  The amount held is the difference of the following:',
  borrowerUsedOutsideFunds:
    'Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender funding amount and our recommendation were the same.  We understand the Borrower used funds outside the loan to pay the Contractor. The amount held is the difference of the following.',
  /** Fallback for a mismatch with no signed adjustment row — doesn't assert increase or reduction. */
  neutral: 'Review the difference, shown as follows:',
};

const DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX =
  'We have adjusted our G702 Review Form accordingly and our current recommendation for funding has taken this adjustment into account.';

const DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_SUFFIX =
  "We have adjusted our recommendation amount by the difference and recommend that the Borrower use these funds, which they are currently holding, and the recommended funding amount to fund the Contractor for the entire amount of the Contractor's current application.";

const DISCREPANCY_COMMENT_SUFFIXES: Record<DiscrepancyCommentVariant, string> = {
  increase: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  reduction: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  borrowerHolding: DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_SUFFIX,
  borrowerUsedOutsideFunds: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  neutral: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
};

/** One payapp's previous fundings table, reduced to what the comment and the mismatch alert need. */
export interface PreviousFundingTableState {
  drawId: string;
  /** G702 name, printed as "<label> Previous Funding:" when the report covers several payapps. */
  label: string;
  receivedTotal: number;
  fundedByLenderTotal: number;
  /** CDCC's recommended total — excludes adjustment rows, whose Funded by Lender only records how the discrepancy was resolved. */
  recommendedTotal: number;
  /** Received by Contractor minus Funded by Lender. */
  difference: number;
  hasAdjustmentRow: boolean;
  latestAdjustmentAmount: number;
  /** Funded by Lender matches Received by Contractor on the latest adjustment row. */
  latestAdjustmentResolved: boolean;
  /** An adjustment row counts even once resolved, so matching totals don't fall back to the plain comment. */
  hasDifference: boolean;
  /** Warning stays up until the latest adjustment row is resolved. */
  showMismatchAlert: boolean;
}

export const summarizePreviousFundingTable = (
  drawId: string,
  label: string,
  fundings: ProjectPreviousFundingDTO[] | undefined,
): PreviousFundingTableState => {
  const rows = fundings || [];
  const receivedTotal = rows.reduce((sum, funding) => sum + (funding.receivedByContractor || 0), 0);
  const fundedByLenderTotal = rows.reduce((sum, funding) => sum + (funding.amountFunded || 0), 0);
  const recommendedTotal = rows.reduce((sum, funding) => (funding.isDiscrepancy ? sum : sum + (funding.amountFunded || 0)), 0);

  // Adjustment rows from earlier reports stay in the table, so only the latest one decides the case —
  // an older negative row must not pin the comment to the negative case after the current row turns positive.
  const adjustmentRows = rows.filter(funding => funding.isDiscrepancy).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const latestAdjustmentRow = adjustmentRows[adjustmentRows.length - 1];
  const hasAdjustmentRow = adjustmentRows.length > 0;
  const latestAdjustmentAmount = latestAdjustmentRow?.receivedByContractor ?? 0;
  // Keys off the entered value, not a manual toggle — resolved once amounts match.
  const latestAdjustmentResolved = hasAdjustmentRow && Math.abs((latestAdjustmentRow?.amountFunded ?? 0) - latestAdjustmentAmount) < 0.01;

  const difference = receivedTotal - fundedByLenderTotal;
  const hasDifference = hasAdjustmentRow || Math.abs(difference) >= 0.01;

  return {
    drawId,
    label,
    receivedTotal,
    fundedByLenderTotal,
    recommendedTotal,
    difference,
    hasAdjustmentRow,
    latestAdjustmentAmount,
    latestAdjustmentResolved,
    hasDifference,
    showMismatchAlert: hasDifference && !latestAdjustmentResolved,
  };
};

/** Resolved (Funded by Lender matches Received by Contractor) → reduction/increase; unresolved → borrowerHolding/borrowerUsedOutsideFunds. */
export const getDiscrepancyCommentVariant = (
  hasNegativeAdjustment: boolean,
  negativeAdjustmentResolved: boolean,
  hasPositiveAdjustment = false,
  positiveAdjustmentResolved = false,
): DiscrepancyCommentVariant => {
  if (hasNegativeAdjustment) return negativeAdjustmentResolved ? 'reduction' : 'borrowerHolding';
  if (hasPositiveAdjustment) return positiveAdjustmentResolved ? 'increase' : 'borrowerUsedOutsideFunds';
  return 'neutral';
};

/**
 * The case for the differing tables together: the sign of the net adjustment across them picks the
 * negative or positive case, and it is resolved only once every differing table is. With one table
 * this is that table's own case.
 */
export const getCombinedDiscrepancyCommentVariant = (differingTables: PreviousFundingTableState[]): DiscrepancyCommentVariant => {
  if (!differingTables.some(table => table.hasAdjustmentRow)) return 'neutral';
  const netAdjustment = differingTables.reduce((sum, table) => sum + table.latestAdjustmentAmount, 0);
  const allResolved = differingTables.every(table => table.latestAdjustmentResolved);
  return getDiscrepancyCommentVariant(
    netAdjustment < 0,
    netAdjustment < 0 && allResolved,
    netAdjustment > 0,
    netAdjustment > 0 && allResolved,
  );
};

const buildTableBlock = (table: PreviousFundingTableState, showLabel: boolean): string => {
  const lines = [
    `Our previous recommendation for funding: ${formatCurrency(table.recommendedTotal.toString())}`,
    `The amount shown received by the Contractor: ${formatCurrency(table.receivedTotal.toString())}`,
  ];
  return (showLabel ? [`${table.label} Previous Funding:`, ...lines] : lines).join('\n');
};

/** Prefix, one recommendation/received block per differing table (labelled unless single-payapp), then the suffix. */
export const buildDiscrepancyAutoComment = (
  differingTables: PreviousFundingTableState[],
  variant: DiscrepancyCommentVariant,
  scope: DiscrepancyCommentScope,
): string => {
  const prefix = `${discrepancyOpening(scope)}  ${DISCREPANCY_COMMENT_PREFIX_BODIES[variant]}`;
  const blocks = differingTables.map(table => buildTableBlock(table, scope !== 'single'));
  return [prefix, ...blocks, DISCREPANCY_COMMENT_SUFFIXES[variant]].join('\n\n');
};

export interface PreviousFundingsAutoComment {
  match: boolean;
  variant: DiscrepancyCommentVariant;
  scope: DiscrepancyCommentScope;
  differingTables: PreviousFundingTableState[];
  comment: string;
  /** Changes whenever the comment's case changes (not its amounts) — a change replaces even an edited note. */
  caseKey: string;
}

export const buildPreviousFundingsAutoComment = (tables: PreviousFundingTableState[]): PreviousFundingsAutoComment => {
  const isMultiPayapp = tables.length > 1;
  const differingTables = tables.filter(table => table.hasDifference);
  const match = differingTables.length === 0;
  const scope: DiscrepancyCommentScope = !isMultiPayapp ? 'single' : differingTables.length === 1 ? 'oneOfMany' : 'many';
  const variant = getCombinedDiscrepancyCommentVariant(differingTables);
  const comment = match
    ? isMultiPayapp
      ? AUTO_COMMENT_MULTI
      : AUTO_COMMENT
    : buildDiscrepancyAutoComment(differingTables, variant, scope);
  const caseKey = match ? 'match' : `${scope}|${variant}|${differingTables.map(table => table.drawId).join(',')}`;

  return { match, variant, scope, differingTables, comment, caseKey };
};

/** Blank, or any auto-comment the current tables could produce — i.e. not hand-edited. */
export const isUneditedPreviousFundingsComment = (comment: string, autoComment: PreviousFundingsAutoComment): boolean => {
  if (!comment.trim() || MATCH_AUTO_COMMENTS.includes(comment)) return true;
  if (autoComment.match) return false;
  return ALL_DISCREPANCY_COMMENT_VARIANTS.some(
    variant => comment === buildDiscrepancyAutoComment(autoComment.differingTables, variant, autoComment.scope),
  );
};
